import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextareaAutosize from 'react-textarea-autosize';
import {
  faShoppingCart,
  faTrash,
  faCreditCard,
  faEnvelope,
  faUpload,
  faPlus,
  faTimes,
  faCheck,
  faArrowLeft,
  faLock,
  faBuilding,
  faUser,
  faMessage,
} from '@fortawesome/free-solid-svg-icons';
import useDashboardStore from 'stores/DashboardStore';
import Currency from 'components/Currency/Currency';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';
import logo from 'assets/images/brand/cauze-logotype-accent.svg';
import { RadioButton, GiftingSection } from './components';
import AmountSelector from 'components/AmountSelector/AmountSelectorMini';
import ImageUploadModal from 'components/modals/ImageUploadModal/ImageUploadModal';

const PRESET_AMOUNTS = [1000, 2500, 5000, 10000, 25000, 50000];
const SUGGESTED_MESSAGES = [
  'Wishing you joy this season! Please accept this gift to support a cause close to your heart.',
  'Happy holidays! Make a difference by donating this gift to your favorite charity.',
  "Thinking of you! Use this to support a cause you're passionate about.",
];

const GiftingView = () => {
  const { setProps } = useDashboardStore();

  // State
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [branding, setBranding] = useState({
    name: '',
    logo: null,
    type: 'individual',
  });
  const [isBrandingExpanded, setIsBrandingExpanded] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [amount, setAmount] = useState(2500);
  const [message, setMessage] = useState('');
  const [recipientMode, setRecipientMode] = useState('single');
  const [singleEmail, setSingleEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutName, setName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isImageUploadModalActive, setIsImageUploadModalActive] =
    useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [showFileError, setShowFileError] = useState(false);
  const uploadImageRef = useRef(null);

  useEffect(() => {
    setProps({
      disableLayout: true,
    });
  }, [setProps]);

  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const addSingleEmail = () => {
    const trimmed = singleEmail.trim();
    if (trimmed && validateEmail(trimmed)) {
      if (!recipients.includes(trimmed)) {
        setRecipients([...recipients, trimmed]);
        setSingleEmail('');
      }
    } else {
      alert('Please enter a valid email address');
    }
  };

  const addBulkEmails = () => {
    const emails = bulkEmails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e && validateEmail(e));

    const uniqueEmails = emails.filter((e) => !recipients.includes(e));

    if (uniqueEmails.length > 0) {
      setRecipients([...recipients, ...uniqueEmails]);
      setBulkEmails('');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        const emails = text
          .split(/[\n,;]/)
          .map((e) => e.trim())
          .filter((e) => e && validateEmail(e));

        const uniqueEmails = emails.filter((e) => !recipients.includes(e));

        if (uniqueEmails.length > 0) {
          setRecipients([...recipients, ...uniqueEmails]);
        }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const removeRecipient = (email) => {
    setRecipients(recipients.filter((r) => r !== email));
  };

  const handlePresetClick = (preset) => {
    setAmount(preset);
    setShowCustomAmount(false);
    setCustomAmount('');
  };

  const handleCustomSubmit = () => {
    const value = parseFloat(customAmount);
    if (value && value > 0) {
      setAmount(value);
      setCustomAmount('');
    }
  };

  const handleAddToCart = () => {
    if (recipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }

    const newGift = {
      id: Date.now().toString(),
      recipients: [...recipients],
      amount,
      message,
    };

    setCart([...cart, newGift]);

    // Reset form
    setRecipients([]);
    setAmount(25);
    setMessage('');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => {
      return total + item.amount * item.recipients.length;
    }, 0);
  };

  const getTotalRecipients = () => {
    return cart.reduce((total, item) => total + item.recipients.length, 0);
  };

  const handleBrandingFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      setBranding({ ...branding, logo: result });
    };
    reader.readAsDataURL(file);
  };

  const handleCheckout = async () => {
    if (!checkoutEmail || !checkoutName) {
      alert('Please fill in all fields');
      return;
    }

    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setIsComplete(true);
  };

  useEffect(() => {
    if (!isImageUploadModalActive && uploadImageRef.current) {
      uploadImageRef.current.value = '';
    }
  }, [isImageUploadModalActive]);

  if (isComplete) {
    return (
      <div className="min-h-screen bg-lightgray-f7f flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-lightgray-e8e p-8 text-center">
          <div className="w-16 h-16 bg-green-34c rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faCheck} className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-agenda-bold text-3xl mb-3 text-cauze-primary">
            Thank You!
          </h2>
          <p className="text-text-light mb-6">
            Your charitable gifts have been sent! Recipients will receive an
            email with instructions to donate to their chosen cause.
          </p>
          <div className="bg-lightgray-f7f rounded-lg p-4 mb-6">
            <div className="text-sm text-text-light mb-1">Total Impact</div>
            <div className="text-3xl font-agenda-bold text-cauze-primary">
              <Currency amount={getTotalAmount()} />
            </div>
            <div className="text-sm text-text-light mt-1">
              to {getTotalRecipients()} recipients
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full cz-btn-brand"
          >
            Send More Gifts
          </button>
        </div>
      </div>
    );
  }

  if (showCheckout) {
    return (
      <div className="min-h-screen bg-lightgray-f7f">
        <header className="bg-white border-b border-lightgray-e8e">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <button
              onClick={() => setShowCheckout(false)}
              className="flex items-center gap-2 text-gray-595 hover:text-gray-1b2 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
              Back to Cart
            </button>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="font-agenda-bold text-3xl mb-8 text-center text-gray-1b2">
            Checkout
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border border-lightgray-e8e p-6">
              <div className="flex items-center gap-2 mb-6">
                <FontAwesomeIcon
                  icon={faLock}
                  className="w-5 h-5 text-green-34c"
                />
                <h3 className="text-xl font-agenda-semibold text-gray-1b2">
                  Secure Payment
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-agenda-medium text-gray-595 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={checkoutName}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border border-lightgray-c4c rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent font-agenda"
                  />
                </div>

                <div>
                  <label className="block text-sm font-agenda-medium text-gray-595 mb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={checkoutEmail}
                    onChange={(e) => setCheckoutEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-lightgray-c4c rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent font-agenda"
                  />
                </div>

                <div className="pt-4">
                  <button
                    onClick={handleCheckout}
                    disabled={isProcessing}
                    className="w-full cz-btn-brand"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center gap-2">
                        <CauzeSpinner
                          style={{
                            width: '20px',
                            height: '20px',
                          }}
                        />
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <FontAwesomeIcon
                          icon={faCreditCard}
                          className="w-5 h-5"
                        />
                        Complete Purchase -{' '}
                        <Currency amount={getTotalAmount()} />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-lightgray-e8e p-6">
              <h3 className="font-agenda-semibold text-xl mb-4 text-gray-1b2">
                Order Summary
              </h3>

              {branding.name && (
                <div className="bg-lightgray-f7f rounded-lg p-4 mb-4">
                  <div className="text-xs text-gray-595 mb-2">Gift from:</div>
                  <div className="flex items-center gap-3">
                    {branding.logo ? (
                      <img
                        src={branding.logo}
                        alt={branding.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-cauze-primary flex items-center justify-center text-white text-lg font-agenda-bold">
                        {branding.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-agenda-medium">{branding.name}</div>
                      <div className="text-xs text-gray-595">
                        {branding.type === 'company' ? 'Company' : 'Individual'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-lightgray-f7f rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-sm text-gray-595">
                          {item.recipients.length}{' '}
                          {item.recipients.length === 1
                            ? 'recipient'
                            : 'recipients'}
                        </div>
                        <div className="text-lg font-agenda-semibold text-cauze-primary">
                          <Currency amount={item.amount} /> each
                        </div>
                      </div>
                      <div className="text-right font-agenda-semibold">
                        <Currency
                          amount={item.amount * item.recipients.length}
                        />
                      </div>
                    </div>
                    {item.message && (
                      <div className="text-xs text-gray-595 italic truncate">
                        "{item.message}"
                      </div>
                    )}
                  </div>
                ))}

                <div className="pt-4 border-t border-lightgray-e8e space-y-2">
                  <div className="flex justify-between text-sm text-gray-595">
                    <span>Total Recipients</span>
                    <span>{getTotalRecipients()}</span>
                  </div>
                  <div className="flex justify-between text-xl pt-2">
                    <span className="font-agenda-semibold text-gray-1b2">
                      Total
                    </span>
                    <span className="font-agenda-bold text-cauze-primary">
                      <Currency amount={getTotalAmount()} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ImageUploadModal
        cropRatio={[1, 1]}
        isActive={isImageUploadModalActive}
        onToggleClose={() => setIsImageUploadModalActive(false)}
        onSave={() => {}}
        upImg={imageUrl}
        setUpImg={setImageUrl}
        updateImageUrl={setImageUrl}
        showFileError={showFileError}
        setShowFileError={setShowFileError}
        onFileUpload={handleBrandingFileUpload}
        isChanged={true}
      />
      <div className="h-screen gifting-view bg-lightgray-f7f flex-1 overflow-y-auto font-agenda">
        {/* Header */}
        <header className="bg-white border-b border-lightgray-e8e sticky top-0">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Cauze" className="h-8 w-auto" />
              </div>
            </div>
          </div>
        </header>

        {/* Hero */}
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <h2 className="font-agenda-bold text-4xl md:text-5xl mb-4 text-gray-1b2">
            Give the Gift of Giving
          </h2>
          <p className="text-lg text-gray-595 max-w-2xl mx-auto">
            Send charitable gift cards to the people you care about. They choose
            the cause, you change the world together.
          </p>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pb-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Gift Selection */}
            <div className="lg:col-span-2">
              {/* Branding Section */}
              {!isBrandingExpanded && branding.name ? (
                <div className="bg-white rounded-2xl shadow-sm border border-lightgray-e8e p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {branding.logo ? (
                        <img
                          src={branding.logo}
                          alt={branding.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-lightgray-e8e"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-cauze-primary flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={
                              branding.type === 'company' ? faBuilding : faUser
                            }
                            className="w-8 h-8 text-white"
                          />
                        </div>
                      )}
                      <div>
                        <div className="text-sm text-gray-595">
                          {branding.type === 'company'
                            ? 'Company'
                            : 'Individual'}
                        </div>
                        <div className="text-xl font-agenda-semibold text-gray-1b2">
                          {branding.name}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsBrandingExpanded(true)}
                      className="px-4 py-2 text-cauze-primary hover:bg-lightgray-f7f rounded-lg transition-colors font-agenda-medium"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ) : (
                <GiftingSection
                  title="Your Branding"
                  subtitle="Add your logo and name to personalize your charitable gifts"
                >
                  <div>
                    <label className="block text-sm font-agenda-medium text-gray-595 mb-3">
                      I am giving as:
                    </label>
                    <div className="flex gap-3">
                      <RadioButton
                        icon={faUser}
                        label="Individual"
                        onClick={() =>
                          setBranding({ ...branding, type: 'individual' })
                        }
                        selected={branding.type === 'individual'}
                      />
                      <RadioButton
                        icon={faBuilding}
                        label="Company"
                        onClick={() =>
                          setBranding({ ...branding, type: 'company' })
                        }
                        selected={branding.type === 'company'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-agenda-medium text-gray-595 mb-2">
                      {branding.type === 'company'
                        ? 'Company Name'
                        : 'Your Name'}
                    </label>
                    <input
                      type="text"
                      value={branding.name}
                      onChange={(e) =>
                        setBranding({ ...branding, name: e.target.value })
                      }
                      placeholder={
                        branding.type === 'company'
                          ? 'Acme Corporation'
                          : 'John Doe'
                      }
                      className="w-full px-4 py-3 border leading-[22px] border-solid border-lightgray-c4c rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent font-agenda"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-agenda-medium text-gray-595 mb-2">
                      {branding.type === 'company'
                        ? 'Company Logo'
                        : 'Profile Picture'}{' '}
                      (Optional)
                    </label>

                    {!branding.logo ? (
                      <label className="flex flex-col w-full px-6 py-8 border-2 border-dashed border-lightgray-c4c rounded-lg text-center cursor-pointer hover:border-cauze-primary hover:bg-lightgray-f7f transition-colors">
                        <FontAwesomeIcon
                          size="3x"
                          icon={faUpload}
                          className="w-8 h-8 mx-auto mb-2 text-gray-595"
                        />
                        <span className="text-gray-595">
                          Click to upload{' '}
                          {branding.type === 'company' ? 'logo' : 'photo'}
                        </span>
                        <p className="text-sm text-gray-595 mt-1">
                          PNG, JPG or GIF
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            setIsImageUploadModalActive(true);
                            setImageUrl(URL.createObjectURL(e.target.files[0]));
                          }}
                          className="hidden"
                          ref={uploadImageRef}
                        />
                      </label>
                    ) : (
                      <div className="relative inline-block">
                        <img
                          src={branding.logo}
                          alt="Preview"
                          className="w-32 h-32 rounded-lg object-cover border-2 border-lightgray-e8e"
                        />
                        <button
                          onClick={() =>
                            setBranding({ ...branding, logo: null })
                          }
                          className="text-lightgray-e8e hover:text-gray-148 ml-2 shrink-0 bg-transparent border-none cursor-pointer top-0 right-0 absolute"
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </GiftingSection>
              )}

              {/* Gift Selection Form */}
              <GiftingSection
                title="Create Your Gift"
                subtitle="Customize your charitable gift card in three simple steps"
              >
                {/* Step 1: Recipients */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-lightgray-f7f text-cauze-primary rounded-full flex items-center justify-center shrink-0 font-agenda-bold">
                      1
                    </div>
                    <h4 className="font-agenda-semibold text-xl text-gray-1b2">
                      Who are you sending to?
                    </h4>
                  </div>
                  <div className="space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      <RadioButton
                        icon={faEnvelope}
                        onClick={() => setRecipientMode('single')}
                        selected={recipientMode === 'single'}
                        label="One at a time"
                      />
                      <RadioButton
                        icon={faPlus}
                        onClick={() => setRecipientMode('paste')}
                        selected={recipientMode === 'paste'}
                        label="Paste multiple"
                      />
                      <RadioButton
                        icon={faUpload}
                        onClick={() => setRecipientMode('upload')}
                        selected={recipientMode === 'upload'}
                        label="Upload file"
                      />
                    </div>

                    {recipientMode === 'single' && (
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={singleEmail}
                          onChange={(e) => setSingleEmail(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === 'Enter' && addSingleEmail()
                          }
                          placeholder="Enter email address"
                          className="flex-1 px-4 py-3 border leading-[22px] border-lightgray-c4c border-solid rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent font-agenda"
                        />
                        <button
                          className="cz-btn-brand px-8"
                          onClick={addSingleEmail}
                        >
                          Add
                        </button>
                      </div>
                    )}

                    {recipientMode === 'paste' && (
                      <div className="space-y-2">
                        <textarea
                          value={bulkEmails}
                          onChange={(e) => setBulkEmails(e.target.value)}
                          placeholder="Paste email addresses (separated by commas, semicolons, or new lines)"
                          className="w-full px-4 py-3 border border-lightgray-c4c rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent min-h-[120px] font-agenda"
                        />
                        <button
                          onClick={addBulkEmails}
                          className="w-full cz-btn-brand"
                        >
                          Add All
                        </button>
                      </div>
                    )}

                    {recipientMode === 'upload' && (
                      <div>
                        <label className="flex flex-column w-full px-6 py-8 border-2 border-dashed border-lightgray-c4c rounded-lg text-center cursor-pointer hover:border-cauze-primary hover:bg-lightgray-f7f transition-colors">
                          <FontAwesomeIcon
                            size="3x"
                            icon={faUpload}
                            className="w-8 h-8 mx-auto mb-2 text-gray-595"
                          />
                          <span className="text-gray-595">
                            Click to upload a file
                          </span>
                          <p className="text-sm text-gray-595 mt-1">
                            CSV or TXT file with email addresses
                          </p>
                          <input
                            type="file"
                            accept=".csv,.txt"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}

                    {recipients.length > 0 && (
                      <div className="bg-lightgray-f7f rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-gray-595">
                            {recipients.length}{' '}
                            {recipients.length === 1
                              ? 'recipient'
                              : 'recipients'}{' '}
                            added
                          </span>
                          <button
                            onClick={() => setRecipients([])}
                            className="cz-btn-outline brand text-sm !py-1.5 flex"
                          >
                            <span className="my-auto h-4">Clear all</span>
                          </button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {recipients.map((email) => (
                            <div
                              key={email}
                              className="flex items-center justify-between bg-white px-3 py-2 rounded-lg"
                            >
                              <span className="text-sm truncate font-agenda">
                                {email}
                              </span>
                              <button
                                onClick={() => removeRecipient(email)}
                                className="text-gray-595 hover:text-red-600 ml-2 shrink-0 bg-transparent border-none cursor-pointer"
                              >
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 2: Amount */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-lightgray-f7f text-cauze-primary rounded-full flex items-center justify-center shrink-0 font-agenda-bold">
                      2
                    </div>
                    <h4 className="font-agenda-semibold text-xl text-gray-1b2">
                      Choose gift amount
                    </h4>
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <AmountSelector
                        amounts={PRESET_AMOUNTS}
                        defaultAmount={amount}
                        rows={2}
                        onAmountUpdate={setAmount}
                      />
                    </div>

                    <div className="bg-lightgray-f7f rounded-lg p-4 text-center">
                      <div className="text-sm text-gray-595 mb-1">
                        Selected Amount
                      </div>
                      <div className="text-3xl font-agenda-bold text-cauze-primary">
                        <Currency amount={amount} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Message */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-lightgray-f7f text-cauze-primary rounded-full flex items-center justify-center shrink-0 font-agenda-bold">
                      3
                    </div>
                    <h4 className="font-agenda-semibold text-xl text-gray-1b2">
                      Add a personal message
                    </h4>
                  </div>
                  <div className="space-y-4">
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faMessage}
                        className="absolute left-3 top-3 w-5 h-5 text-gray-595"
                      />
                      <TextareaAutosize
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Write your personal message here (optional)"
                        className="w-full pl-12 leading-[22px] pr-4 py-3 border border-lightgray-c4c rounded-lg focus:outline-none focus:ring-2 focus:ring-cauze-primary focus:border-transparent min-h-[120px] resize-none font-agenda"
                      />
                    </div>

                    {!message && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-595">
                          Suggested messages:
                        </p>
                        <div className="space-y-2">
                          {SUGGESTED_MESSAGES.map((suggested) => (
                            <RadioButton
                              key={suggested}
                              onClick={() => setMessage(suggested)}
                              label={`"${suggested}"`}
                              className="!h-[44px] !pt-3.5"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={recipients.length === 0}
                  className="w-full cz-btn-brand"
                >
                  <div className="flex items-center justify-center gap-2">
                    <FontAwesomeIcon
                      icon={faShoppingCart}
                      className="w-5 h-5"
                    />
                    Add to Cart ({recipients.length}{' '}
                    {recipients.length === 1 ? 'recipient' : 'recipients'})
                  </div>
                </button>

                {showSuccess && (
                  <div className="bg-green-34c bg-opacity-10 border border-green-34c text-green-107 px-4 py-3 rounded-xl text-center font-agenda-medium">
                    ✓ Added to cart successfully!
                  </div>
                )}
              </GiftingSection>
            </div>

            {/* Cart Sidebar */}
            <div className="lg:col-span-1">
              {cart.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-lightgray-e8e p-8 sticky top-24">
                  <div className="text-center text-gray-595">
                    <FontAwesomeIcon
                      size="3x"
                      icon={faShoppingCart}
                      className="w-12 h-12 mx-auto mb-3 opacity-50"
                    />
                    <p className="font-agenda-medium">Your cart is empty</p>
                    <p className="text-sm mt-1">Add gifts to get started</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-lightgray-e8e overflow-hidden sticky top-24">
                  <div className="bg-cauze-primary px-6 py-4 text-white">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faShoppingCart}
                        className="w-5 h-5"
                      />
                      <h3 className="font-agenda-bold text-xl">Your Cart</h3>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="bg-lightgray-f7f rounded-lg p-4 space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-595">
                                {item.recipients.length}{' '}
                                {item.recipients.length === 1
                                  ? 'recipient'
                                  : 'recipients'}
                              </div>
                              <div className="text-lg font-agenda-semibold text-cauze-primary">
                                <Currency amount={item.amount} /> each
                              </div>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-gray-595 hover:text-red-600 transition-colors shrink-0 bg-transparent border-none cursor-pointer"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="w-4 h-4"
                              />
                            </button>
                          </div>

                          <div className="text-xs text-gray-595 truncate">
                            To: {item.recipients.slice(0, 2).join(', ')}
                            {item.recipients.length > 2 &&
                              ` +${item.recipients.length - 2} more`}
                          </div>

                          {item.message && (
                            <div className="text-xs text-gray-595 truncate italic">
                              "{item.message}"
                            </div>
                          )}

                          <div className="pt-2 border-t border-lightgray-e8e flex justify-between items-center">
                            <span className="text-sm text-gray-595">
                              Subtotal
                            </span>
                            <span className="text-sm font-agenda-semibold">
                              <Currency
                                amount={item.amount * item.recipients.length}
                              />
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-lightgray-e8e space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-595">Total Recipients</span>
                        <span className="font-agenda-semibold">
                          {getTotalRecipients()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xl">
                        <span className="font-agenda-semibold text-gray-1b2">
                          Total
                        </span>
                        <span className="font-agenda-bold text-cauze-primary">
                          <Currency amount={getTotalAmount()} />
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => setShowCheckout(true)}
                      className="w-full cz-btn-brand"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <FontAwesomeIcon
                          icon={faCreditCard}
                          className="w-5 h-5"
                        />
                        Proceed to Checkout
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftingView;
